import { Onboarding } from "sdk/onboardings";
import { routes } from "~/constants";
import { emailTrackerStem } from "~/OnboardingApp/events";
import {
  postpartumOption,
  pregnantOption,
  shouldSeeDueDatePage,
} from "~/OnboardingApp/utils";
import {
  emptyPhase,
  logisticsPhase,
  welcomePhase,
  yourFertilityJourneyPhase,
} from "./constants";

type OnboardingMeta = {
  analyticsStem: string;
  canRevisit: (onboarding: Partial<Onboarding>) => boolean;
  excludeFromBack?: boolean;
  excludeFromProgress?: boolean;
  excludeNav?: boolean;
  isAnswered: (onboarding: Partial<Onboarding>) => boolean;
  isOptional?: boolean;
  isPublic?: boolean;
  phase: string;
};
export interface MetaByPath {
  [key: string]: OnboardingMeta;
}

// Journeys for which the NextMilestonePage is skipped: "Other" has no milestone
// options, "fertility_check" users are still in diagnostics so there is no
// treatment milestone to ask about yet, and "surrogacy" is not yet modeled as its
// own treatment journey (it derives to the "other" kind on the backend). Values are
// the internal TreatmentPlanAnswers enum values; "Other" reads back lowercased on revisit.
const JOURNEYS_SKIPPING_NEXT_MILESTONE = ["other", "fertility_check", "surrogacy"];

const journeySkipsNextMilestone = (journeyLooksLike?: string[]) => {
  const selectedJourney = (journeyLooksLike?.[0] || "").toLowerCase();
  return JOURNEYS_SKIPPING_NEXT_MILESTONE.includes(selectedJourney);
};

export const metaByPath: MetaByPath = {
  [routes.onboardingRoot]: {
    analyticsStem: "onboarding-root-page",
    canRevisit: () => false,
    isPublic: false,
    isAnswered: () => true,
    phase: welcomePhase,
  },
  // [routes.interstitialWelcome]: {
  //   analyticsStem: "onboarding-welcome-page",
  //   canRevisit: () => true,
  //   isPublic: true,
  //   isAnswered: () => true,
  //   isOptional: true,
  //   phase: welcomePhase,
  // },
  [routes.questionName]: {
    analyticsStem: "onboarding-name-page",
    canRevisit: () => true,
    isPublic: true,
    isAnswered: ({ firstName, lastName }) => !!(firstName && lastName),
    phase: welcomePhase,
  },
  [routes.questionEmail]: {
    analyticsStem: emailTrackerStem,
    isPublic: true,
    canRevisit: ({ email }) => !email,
    isAnswered: ({ email }) => !!email,
    phase: welcomePhase,
  },
  [routes.questionClinicRefer]: {
    analyticsStem: "onboarding-clinic-refer-page",
    canRevisit: () => true,
    isAnswered: ({ clinicRefer }) => !!clinicRefer,
    phase: yourFertilityJourneyPhase,
  },
  [routes.questionCurrentClinic]: {
    analyticsStem: "onboarding-current-clinic-page",
    canRevisit: () => true,
    isAnswered: ({ currentClinic }) => !!currentClinic,
    isOptional: true,
    phase: yourFertilityJourneyPhase,
  },
  [routes.questionHowLong]: {
    analyticsStem: "onboarding-how-long-page",
    canRevisit: () => true,
    isAnswered: ({ lengthOfTimeTtc }) => !!lengthOfTimeTtc,
    phase: yourFertilityJourneyPhase,
  },
  [routes.questionDueDate]: {
    analyticsStem: "onboarding-due-date-page",
    canRevisit: ({ lengthOfTimeTtc }) =>
      !!lengthOfTimeTtc && shouldSeeDueDatePage(lengthOfTimeTtc),
    isAnswered: ({ dueDate, lengthOfTimeTtc }) =>
      !!dueDate?.length ||
      (!!lengthOfTimeTtc && !shouldSeeDueDatePage(lengthOfTimeTtc)),
    phase: yourFertilityJourneyPhase,
    isOptional: true,
  },
  [routes.questionProvidersSeen]: {
    analyticsStem: "onboarding-providers-seen-page",
    canRevisit: () => true,
    isAnswered: ({ providersSeen }) => !!providersSeen?.length,
    phase: yourFertilityJourneyPhase,
  },
  // [routes.questionClinicSatisfaction]: {
  //   analyticsStem: "onboarding-clinic-satisfaction-page",
  //   canRevisit: ({ providersSeen }) => !!providersSeen?.includes(reiClinic),
  //   isAnswered: ({ clinicSatisfaction, providersSeen }) =>
  //     (!!providersSeen?.length && !providersSeen?.includes(reiClinic)) ||
  //     !!clinicSatisfaction,
  //   phase: yourFertilityJourneyPhase,
  // },
  [routes.questionJourneyLooksLike]: {
    analyticsStem: "onboarding-journey-looks-like-page",
    canRevisit: ({ lengthOfTimeTtc }) =>
      lengthOfTimeTtc != pregnantOption && lengthOfTimeTtc != postpartumOption,
    isAnswered: ({ lengthOfTimeTtc, journeyLooksLike }) =>
      lengthOfTimeTtc == pregnantOption ||
      lengthOfTimeTtc == postpartumOption ||
      !!journeyLooksLike?.length,
    phase: yourFertilityJourneyPhase,
  },
  [routes.questionNextMilestone]: {
    analyticsStem: "onboarding-next-milestone-page",
    canRevisit: ({ journeyLooksLike }) =>
      !journeySkipsNextMilestone(journeyLooksLike),
    isAnswered: ({ upcomingMilestone, journeyLooksLike }) =>
      !!upcomingMilestone?.length || journeySkipsNextMilestone(journeyLooksLike),
    phase: yourFertilityJourneyPhase,
  },

  [routes.questionMoreTreatmentDetails]: {
    analyticsStem: "onboarding-more-treatment-details-page",
    canRevisit: () => true,
    // Optional question — selecting nothing must not block progress.
    isAnswered: ({ initialTreatmentTags }) => !!initialTreatmentTags?.length,
    isOptional: true,
    phase: yourFertilityJourneyPhase,
  },

  [routes.questionSupportNeeds]: {
    analyticsStem: "onboarding-support-needs-page",
    canRevisit: () => true,
    isAnswered: ({ supportNeeds }) => !!supportNeeds?.length,
    phase: yourFertilityJourneyPhase,
  },

  [routes.questionMostChallenging]: {
    analyticsStem: "onboarding-most-challenging-page",
    canRevisit: () => true,
    isAnswered: ({ biggestChallenge }) => !!biggestChallenge,
    isOptional: true,
    phase: yourFertilityJourneyPhase,
  },

  [routes.questionClinicSatisfaction]: {
    analyticsStem: "onboarding-clinic-satisfaction-page",
    // Skip when the user has no current clinic — there is nothing to rate.
    canRevisit: ({ currentClinic }) => currentClinic !== "No",
    isAnswered: ({ clinicSatisfaction, currentClinic }) =>
      currentClinic === "No" || !!clinicSatisfaction,
    phase: yourFertilityJourneyPhase,
  },
  // [routes.questionHowSupported]: {
  //   analyticsStem: "onboarding-how-supported-page",
  //   canRevisit: () => true,
  //   isAnswered: ({ howSupported }) => !!howSupported,
  //   phase: yourFertilityJourneyPhase,
  // },
  // [routes.questionHowOptimistic]: {
  //   analyticsStem: "onboarding-how-optimistic-page",
  //   canRevisit: () => true,
  //   isAnswered: ({ howOptimistic }) => !!howOptimistic,
  //   phase: yourFertilityJourneyPhase,
  // },
  // [routes.questionHowAnxious]: {
  //   analyticsStem: "onboarding-how-anxious-page",
  //   canRevisit: () => true,
  //   isAnswered: ({ howAnxious }) => !!howAnxious,
  //   phase: yourFertilityJourneyPhase,
  // },
  // [routes.questionHowGrateful]: {
  //   analyticsStem: "onboarding-how-grateful-page",
  //   canRevisit: () => true,
  //   isAnswered: ({ howGrateful }) => !!howGrateful,
  //   phase: yourFertilityJourneyPhase,
  // },
  // [routes.interstitialLogistics]: {
  //   analyticsStem: "onboarding-logistics-page",
  //   canRevisit: () => true,
  //   isAnswered: ({ howGrateful }) => !!howGrateful,
  //   isOptional: true,
  //   phase: emptyPhase,
  // },
  [routes.questionBirthday]: {
    analyticsStem: "onboarding-birthday-page",
    canRevisit: () => true,
    isAnswered: ({ bornOn }) => !!bornOn,
    phase: logisticsPhase,
  },
  // [routes.questionEmployer]: {
  //   analyticsStem: "onboarding-employer-page",
  //   canRevisit: () => true,
  //   isAnswered: ({ employer }) => !!employer,
  //   isOptional: true,
  //   phase: logisticsPhase,
  // },
  // [routes.questionCoverage]: {
  //   analyticsStem: "onboarding-coverage-page",
  //   canRevisit: ({ lengthOfTimeTtc }) =>
  //     lengthOfTimeTtc != pregnantOption && lengthOfTimeTtc != postpartumOption,
  //   isAnswered: ({ lengthOfTimeTtc, fertilityCoverage }) =>
  //     lengthOfTimeTtc == pregnantOption ||
  //     lengthOfTimeTtc == postpartumOption ||
  //     !!fertilityCoverage?.length,
  //   phase: logisticsPhase,
  // },
  [routes.questionPhone]: {
    analyticsStem: "onboarding-phone-page",
    canRevisit: () => true,
    isAnswered: ({ international, phone }) => international || !!phone,
    phase: logisticsPhase,
  },
  // [routes.questionZip]: {
  //   analyticsStem: "onboarding-zip-page",
  //   canRevisit: () => true,
  //   isAnswered: ({ zipCode }) => !!zipCode,
  //   phase: logisticsPhase,
  // },
  [routes.questionHowDidYouHear]: {
    analyticsStem: "onboarding-how-did-you-hear-page",
    canRevisit: () => true,
    isAnswered: ({ howDidYouHear }) => !!howDidYouHear,
    phase: logisticsPhase,
  },
  [routes.interstitialThanksForSharing]: {
    analyticsStem: "onboarding-thanks-for-sharing-page",
    canRevisit: () => true,
    isAnswered: ({ howDidYouHear }) => !!howDidYouHear,
    phase: emptyPhase,
    excludeFromBack: true,
    excludeFromProgress: true,
    excludeNav: true,
  },
};
